import { FileDownload } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";
import { type FC, useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import EmploymentTypeSelector from "../../components/employmentTypeSelector/EmploymentTypeSelector";
import { useCallInReasons } from "../../components/team/CallInReasons/callInReasonsHooks";
import { TEAM_SECTIONS } from "../../components/team/config";
import { DoubleClickDeleteIcon } from "../../components/UIToolkit/Clickable/DoubleClickDeleteIcon";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useCallOffSourceLabel } from "../../hooks/useCallOffSourceLabel";
import { CALL_INS_PAGE_SIZE } from "../../hooks/useGetLimitedCallOffs";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { NEUTRAL, SURFACE } from "../../theme/tokens";
import type { CalledOffShift } from "../../utils/models";
import { useLastCallInsState } from "../Profile/v2/LastCallIns/useLastCallInsState";

type SortField = keyof CalledOffShift;
type SortOrder = "asc" | "desc";

type TableHeaderProps = {
	field: SortField;
	label: string;
	sortField: SortField;
	sortOrder: SortOrder;
	onSort: (field: SortField) => void;
};

const TableHeader: FC<TableHeaderProps> = ({ field, label, sortField, sortOrder, onSort }) => (
	<TableCell>
		<TableSortLabel
			active={sortField === field}
			direction={sortField === field ? sortOrder : "asc"}
			onClick={() => onSort(field)}
			sx={(theme) => ({
				"& .MuiTableSortLabel-icon": {
					color: `${theme.palette.primary.dark} !important`,
				},
				color: theme.palette.primary.dark,
				fontWeight: "bold",
			})}
		>
			{label}
		</TableSortLabel>
	</TableCell>
);

// Synthetic filter option that, when selected, surfaces historical free-text call-in messages
// (e.g. "switch", "shift swap") which don't match any currently-configured reason label.
const LEGACY_OPTION = "Other (legacy)";

export const CallIns: FC = () => {
	const { t } = useTranslation();
	const callOffSourceLabel = useCallOffSourceLabel();
	const { getNurseById, getNurseNameFromId } = useHospitalData();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { data: callInReasons = [] } = useCallInReasons();
	const legacyOptionLabel = t("call.ins.filter.legacy", LEGACY_OPTION);
	const reasonOptions = useMemo(() => [...callInReasons.map((reason) => reason.label), legacyOptionLabel], [callInReasons, legacyOptionLabel]);
	const configuredLabels = useMemo(() => new Set(callInReasons.map((reason) => reason.label.toLowerCase())), [callInReasons]);
	const [sortField, setSortField] = useState<SortField>("deletedAt");
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
	const [_nameFilter, setNameFilter] = useState<string>("");
	const [dateFromFilter, setDateFromFilter] = useState<DateTime | null>(null);
	const [dateToFilter, setDateToFilter] = useState<DateTime | null>(null);
	const [messageFilters, setMessageFilters] = useState<string[]>([]);
	const [employmentTypeSections, setEmploymentTypeSections] = useState<Set<string>>(new Set());
	const { rawData, isLoadingGetLimitedCallOffs, isErrorGetLimitedCallOffs, handleDiscardNurseRemove } = useLastCallInsState({
		limitRows: CALL_INS_PAGE_SIZE,
	});
	const nameFilter = useDeferredValue(_nameFilter);

	const handleSort = (field: SortField): void => {
		const newSortOrder = field === sortField && sortOrder === "asc" ? "desc" : "asc";
		setSortField(field);
		setSortOrder(newSortOrder);
	};

	const handleExportToExcel = (): void => {
		if (!rawData.length) return;

		const headers = [
			"#",
			"Name",
			...(jobManagementEnabled ? ["Job"] : []),
			"Start Time",
			"End Time",
			"Shift Type",
			"Call-in Time",
			"Source",
			"Reason",
			"Internal Notes",
		];
		const csvContent = [
			headers.join(","),
			...filteredAndSortedEntries.map((entry, index) => {
				const nurse = getNurseById(entry.nurseId);
				const jobNames = nurse?.jobs?.map((job) => job.displayName).join(", ") || "—";
				return [
					index + 1,
					`"${getNurseNameFromId(entry.nurseId)}"`,
					...(jobManagementEnabled ? [`"${jobNames}"`] : []),
					`"${entry.startTime.toFormat("MMM D, yyyy HH:mm")}"`,
					`"${entry.endTime.toFormat("MMM D, yyyy HH:mm")}"`,
					`"${entry.shiftType}"`,
					`"${(entry.deletedAt ?? entry.updatedAt).toFormat("MMM D, yyyy HH:mm")}"`,
					`"${callOffSourceLabel(entry.deletedSource)}"`,
					`"${entry.message || ""}"`,
					`"${entry.removalMessage || ""}"`,
				].join(",");
			}),
		].join("\n");

		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", `call-ins-${DateTime.now().toFormat("yyyy-MM-dd")}.csv`);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const filteredAndSortedEntries = useMemo<CalledOffShift[]>(() => {
		let entries = [...(rawData || [])];

		if (nameFilter) {
			entries = entries.filter((entry) => getNurseNameFromId(entry.nurseId).toLowerCase().includes(nameFilter.toLowerCase()));
		}

		if (dateFromFilter) {
			entries = entries.filter((entry) => entry.startTime >= dateFromFilter);
		}

		if (dateToFilter) {
			entries = entries.filter((entry) => entry.endTime <= dateToFilter);
		}

		if (messageFilters.length > 0) {
			const legacySelected = messageFilters.includes(legacyOptionLabel);
			const explicitLabels = messageFilters.filter((label) => label !== legacyOptionLabel);
			entries = entries.filter((entry) => {
				if (!entry.message) return false;
				if (explicitLabels.includes(entry.message)) return true;
				if (legacySelected && !configuredLabels.has(entry.message.toLowerCase())) return true;
				return false;
			});
		}

		if (employmentTypeSections.size) {
			entries = entries.filter((entry) => {
				const nurse = getNurseById(entry.nurseId);
				if (!nurse?.employmentType) return false;

				return Array.from(employmentTypeSections).some((sectionTitle) => {
					const section = TEAM_SECTIONS.find((s) => s.title === sectionTitle);
					return section?.employmentTypes.includes(nurse.employmentType);
				});
			});
		}

		return entries.sort((a, b) => {
			const compareValue = sortOrder === "asc" ? 1 : -1;

			if (sortField === "deletedAt") {
				// deletedAt is the real call-in time; fall back to updatedAt for the rare
				// legacy row where it's missing (mirrors the column's display value).
				const aTime = a.deletedAt ?? a.updatedAt;
				const bTime = b.deletedAt ?? b.updatedAt;
				return aTime.diff(bTime, "minute").minutes * compareValue;
			}

			if (sortField === "startTime" || sortField === "endTime" || sortField === "updatedAt") {
				return a[sortField].diff(b[sortField], "minute").minutes * compareValue;
			}

			if (sortField === "nurseId") {
				const nameA = getNurseNameFromId(a.nurseId);
				const nameB = getNurseNameFromId(b.nurseId);
				return (nameA > nameB ? 1 : -1) * compareValue;
			}

			return ((a[sortField] ?? "") > (b[sortField] ?? "") ? 1 : -1) * compareValue;
		});
	}, [
		rawData,
		sortField,
		sortOrder,
		nameFilter,
		dateFromFilter,
		dateToFilter,
		messageFilters,
		employmentTypeSections,
		getNurseById,
		getNurseNameFromId,
		configuredLabels,
		legacyOptionLabel,
	]);

	const formatTimestamp = (date: DateTime) => {
		const datePart = date.toFormat("ccc MMM d, yyyy");
		const timePart = date.toFormat("h:mm");

		return (
			<Box component="span">
				<Typography component="span" sx={{ opacity: 1, ml: 1 }}>
					{datePart}
				</Typography>
				<Typography component="span" sx={{ opacity: 0.5, ml: 1 }}>
					{timePart}
				</Typography>
			</Box>
		);
	};

	if (isErrorGetLimitedCallOffs) {
		return <Box>Error loading call-ins data</Box>;
	}

	return (
		<Box
			data-testid="call-ins-root"
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				gap: 2,
				p: 1,
			}}
		>
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
					backgroundColor: NEUTRAL[50],
					padding: "16px",
					borderRadius: "4px",
				}}
			>
				<Typography
					sx={(theme) => ({
						color: theme.palette.primary.dark,
						fontSize: "1rem",
						fontWeight: "bold",
					})}
				>
					Call-Ins ({filteredAndSortedEntries.length})
				</Typography>

				<Button
					data-testid="call-ins-export"
					data-annie-target="call-ins-export"
					variant="outlined"
					size="small"
					onClick={handleExportToExcel}
					disabled={!rawData.length}
					startIcon={<FileDownload />}
					sx={(theme) => ({
						color: theme.palette.primary.dark,
						borderColor: theme.palette.primary.dark,
						"&:hover": {
							borderColor: theme.palette.primary.dark,
							backgroundColor: alpha(theme.palette.primary.dark, 0.1),
						},
					})}
				>
					Export to Excel
				</Button>
			</Box>
			<LocalizationProvider dateAdapter={AdapterLuxon}>
				<Box
					data-testid="call-ins-filter-section"
					data-annie-target="call-ins-filters"
					sx={{
						display: "flex",
						gap: 2,
						p: 2,
						backgroundColor: NEUTRAL[50],
						borderRadius: "4px",
						alignItems: "center",
						flexWrap: "wrap",
					}}
				>
					<TextField
						data-testid="call-ins-name-filter-input"
						size="small"
						label="Filter by Name"
						value={_nameFilter}
						onChange={(e) => setNameFilter(e.target.value)}
						sx={{ minWidth: 200 }}
					/>

					<EmploymentTypeSelector onSectionsChange={setEmploymentTypeSections} />

					<Box data-testid="call-ins-from-date-input">
						<DatePicker
							label="From Date"
							value={dateFromFilter}
							onChange={(newValue) => setDateFromFilter(newValue)}
							maxDate={dateToFilter || undefined}
							slotProps={{
								textField: {
									size: "small",
									sx: { minWidth: 200 },
								},
							}}
						/>
					</Box>

					<Box data-testid="call-ins-to-date-input">
						<DatePicker
							label="To Date"
							value={dateToFilter}
							onChange={(newValue) => setDateToFilter(newValue)}
							minDate={dateFromFilter || undefined}
							slotProps={{
								textField: {
									size: "small",
									sx: { minWidth: 200 },
								},
							}}
						/>
					</Box>

					<Autocomplete
						data-testid="call-ins-message-type-filter"
						data-annie-target="call-ins-reason-filter"
						multiple
						size="small"
						options={reasonOptions}
						value={messageFilters}
						onChange={(_, newValue) => setMessageFilters(newValue)}
						renderOption={(props, option, { selected }) => (
							<li {...props}>
								<Checkbox checked={selected} />
								{option}
							</li>
						)}
						renderInput={(params) => <TextField {...params} label="Message Types" placeholder="Select message types" />}
						sx={{ minWidth: 250 }}
					/>

					{(nameFilter || dateFromFilter || dateToFilter || !!messageFilters.length || !!employmentTypeSections.size) && (
						<Button
							data-testid="call-ins-clear-filters-button"
							variant="outlined"
							size="small"
							onClick={() => {
								setNameFilter("");
								setDateFromFilter(null);
								setDateToFilter(null);
								setMessageFilters([]);
								setEmploymentTypeSections(new Set());
							}}
							sx={(theme) => ({
								color: theme.palette.primary.dark,
								borderColor: theme.palette.primary.dark,
								"&:hover": {
									borderColor: theme.palette.primary.dark,
									backgroundColor: alpha(theme.palette.primary.dark, 0.1),
								},
							})}
						>
							Clear Filters
						</Button>
					)}
				</Box>
			</LocalizationProvider>

			<Box
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				<TableContainer
					data-testid="call-ins-table"
					sx={{
						flex: 1,
						overflow: "auto",
						"&::-webkit-scrollbar": {
							width: "6px",
							height: "6px",
						},
						"&::-webkit-scrollbar-track": {
							background: NEUTRAL[100],
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb": {
							background: NEUTRAL[500],
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb:hover": {
							background: NEUTRAL[700],
						},
					}}
				>
					<Table
						size="small"
						stickyHeader
						sx={(theme) => ({
							tableLayout: "fixed",
							width: "100%",
							"& .MuiTableCell-root": {
								boxSizing: "border-box",
								padding: "10px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								textAlign: "center",
							},
							"& thead th": {
								height: "36px",
								background: alpha(theme.palette.primary.main, 0.2),
								borderBottom: `1px solid ${NEUTRAL[200]}`,
								color: theme.palette.primary.dark,
								fontSize: "0.875rem",
								fontWeight: "bold",
								padding: "13px 10px",
								textAlign: "center",
								position: "sticky",
								top: 0,
								zIndex: 1,
								"&:first-of-type": {
									width: "60px",
								},
								"&:last-of-type": {
									width: "80px",
								},
							},
							"& thead th:nth-of-type(n+2):not(:last-of-type)": {
								width: "calc((100% - 60px - 80px) / 8)",
							},
							"& tbody td": {
								height: "36px",
								background: SURFACE,
								borderBottom: `1px solid ${NEUTRAL[200]}`,
								color: theme.palette.primary.dark,
								fontSize: "0.875rem",
								textAlign: "center",
								padding: "6px 10px",
							},
						})}
					>
						<TableHead data-annie-target="call-ins-table-header">
							<TableRow>
								<TableCell>#</TableCell>
								<TableHeader field="nurseId" label="Name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
								{jobManagementEnabled && (
									<TableCell
										sx={(theme) => ({
											color: theme.palette.primary.dark,
											fontWeight: "bold",
										})}
									>
										Job
									</TableCell>
								)}
								<TableHeader field="startTime" label="Start Time" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
								<TableHeader field="endTime" label="End Time" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
								<TableHeader field="shiftType" label="Shift Type" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
								<TableHeader field="deletedAt" label="Call-in Time" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
								<TableCell>Source</TableCell>
								<TableCell>Reason</TableCell>
								<TableCell>Internal Notes</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoadingGetLimitedCallOffs ? (
								<TableRow>
									<TableCell colSpan={jobManagementEnabled ? 11 : 10} sx={{ textAlign: "center", padding: "40px" }}>
										<CircularProgress sx={(theme) => ({ color: theme.palette.primary.dark })} />
									</TableCell>
								</TableRow>
							) : (
								filteredAndSortedEntries.map((entry, index) => {
									const nurse = getNurseById(entry.nurseId);
									const jobNames = nurse?.jobs?.map((job) => job.displayName).join(", ") || "—";
									return (
										<TableRow key={entry.id} hover sx={{ cursor: "pointer" }}>
											<TableCell>{index + 1}</TableCell>
											<TableCell>{getNurseNameFromId(entry.nurseId)}</TableCell>
											{jobManagementEnabled && <TableCell>{jobNames}</TableCell>}
											<TableCell>{formatTimestamp(entry.startTime)}</TableCell>
											<TableCell>{formatTimestamp(entry.endTime)}</TableCell>
											<TableCell>{entry.shiftType}</TableCell>
											<TableCell>{formatTimestamp(entry.deletedAt ?? entry.updatedAt)}</TableCell>
											<TableCell>{callOffSourceLabel(entry.deletedSource)}</TableCell>
											<TableCell>{entry.message || ""}</TableCell>
											<TableCell>{entry.removalMessage || ""}</TableCell>
											<TableCell>
												<DoubleClickDeleteIcon onDeleteConfirmed={() => handleDiscardNurseRemove(entry)} />
											</TableCell>
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Box>
	);
};
